import {
  Action,
  config,
  defaultProcessingSaga,
  defaultResponseProcessing,
  fetchAuthSaga,
  FetchResponse,
} from '@triare/auth-redux';
import {
  put, delay, takeLatest, call,
} from 'redux-saga/effects';
import { actions, CommentEntity, CommentStatus } from './index';
import { PaginatedResponse, PaginationProps } from '../types';
import { Role } from '../../enums/user';

export type CommentChildrenListData = PaginatedResponse<Comment>

export type CommentChildrenListResponse = CommentChildrenListData

export type CommentOrderColumn = 'id' | 'text' | 'likeNumber' | 'dislikeNumber' | 'subCommentCount' | 'createdAt' | 'updatedAt';

export type CommentChildrenListParams = PaginationProps<CommentOrderColumn> & {
  id?: string;
  status?: CommentStatus;
  entity?: CommentEntity;
  role?: Role;
  updatedAtStart?: string;
  updatedAtEnd?: string;
  search?: string;
  business?: string;
  user?: string;
}

export interface CommentChildrenListPayload {
  parentId: string;
  params: CommentChildrenListParams;
}

export const commentChildrenList = (payload: CommentChildrenListPayload): Action<CommentChildrenListPayload> => ({
  type: actions.commentChildrenListTrigger.toString(),
  payload,
});

export const commentChildrenListClear = (): Action => ({
  type: actions.commentChildrenListClear.toString(),
});

export const commentChildrenListErrorClear = (): Action => ({
  type: actions.commentChildrenListErrorReset.toString(),
});

export function* commentChildrenListRequestSaga({ payload }: Action<CommentChildrenListParams>) {
  yield put({
    type: actions.commentChildrenListStart.toString(),
  });

  if (!payload) {
    yield put({
      type: actions.commentChildrenListError.toString(),
      payload: {
        message: 'CommentChildrenList: Parameters not passed.',
      },
    });

    return;
  }

  const { parentId, ...restParams } = payload

  const params = new URLSearchParams(Object.entries(restParams).filter((entry) => !!entry[1]));

  const response = (yield call(
    fetchAuthSaga,
    `${process.env.REACT_APP_API}comments/${parentId}/children?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', },
    },
    {},
    config.fetchDelay,
  )) as FetchResponse;

  yield defaultResponseProcessing<CommentChildrenListData, CommentChildrenListResponse>(
    response,
    actions.commentChildrenListSuccess.toString(),
    actions.commentChildrenListError.toString(),
    false,
    (commentChildrenListData) => commentChildrenListData,
  );
}

export default function* saga() {
  yield takeLatest(
    actions.commentChildrenListTrigger.toString(),
    defaultProcessingSaga(
      commentChildrenListRequestSaga,
      {
        error: actions.commentChildrenListError.toString(),
        locationCancel: actions.commentChildrenListClear.toString(),
      },
    ),
  );

  yield takeLatest(actions.commentChildrenListSuccess.toString(), function* clear() {
    if (config.delaySuccess) {
      yield delay(config.delaySuccess);
    }
  });

  yield takeLatest(actions.commentChildrenListError.toString(), function* errorReset() {
    if (config.delayError) {
      yield delay(config.delayError);
    }

    yield put({
      type: actions.commentChildrenListErrorReset.toString(),
    });
  });
}
